/**
 * ニュース詳細のFV背景にスクロール連動の視差を付ける
 *
 * @description 下へスクロールすると、ぼかした背景だけが少し遅れて下へ流れる。
 *              中央に重なる原寸の画像は動かさないので、二層のあいだに奥行きが出る。
 *
 *              振れ幅は背景が枠の外へはみ出している量をDOMから読んで決める。SCSSと
 *              JSの両方に同じ数値を書くと、片方だけ直したときに枠の縁が見える
 */

import { gsap } from 'gsap/dist/gsap.min.js';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger.min.js';

gsap.registerPlugin(ScrollTrigger);

// 生きているトゥイーン。非同期遷移で古いDOMを捨てるとき、
// 外れた要素を見続けるScrollTriggerが残らないように持っておく
let tween: gsap.core.Tween | null = null;

/**
 * 背景が枠の外へはみ出している量を返す
 *
 * @description 背景はabsoluteで、位置決めの基準は枠そのもの。SCSSが `inset: vw(-48) 0`
 *              を置いているので offsetTop がそのまま負のはみ出し量になる。transformは
 *              offsetTopに影響しないので、動かしている最中に読んでも値は変わらない
 * @param bg 背景のレイヤー
 * @returns はみ出し量(px)。伸ばされていなければ0
 */
const overhangOf = (bg: HTMLElement): number => Math.max(0, -bg.offsetTop);

/**
 * FVの視差を破棄する
 *
 * @returns {void}
 */
const destroyTopicParallax = (): void => {
    tween?.scrollTrigger?.kill();
    tween?.kill();
    tween = null;
};

/**
 * FVの視差を始める
 *
 * @returns {void}
 */
const initTopicParallax = (): void => {
    destroyTopicParallax();

    // アイキャッチが無い記事にはFVごと出ない
    const fv = document.querySelector<HTMLElement>('.topic__fv');
    const bg = fv?.querySelector<HTMLElement>('.topic__fvBg');

    if (!fv || !bg) return;

    // 視差は装飾なので、モーションを抑える設定では動かさない
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    tween = gsap.fromTo(
        bg,
        { y: () => -overhangOf(bg) },
        {
            y: () => overhangOf(bg),
            ease: 'none',
            scrollTrigger: {
                trigger: fv,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                // はみ出し量は画面幅依存(vw)なので、リサイズ時に測り直させる
                invalidateOnRefresh: true,
            },
        },
    );
};

export { initTopicParallax, destroyTopicParallax };

document.addEventListener('DOMContentLoaded', initTopicParallax);
